import { useEffect } from 'react';

import { useEffectOnce } from 'hooks/useEffectOnce';
import { usePrevious } from 'hooks/usePrevious';
import isEqual from 'lodash/isEqual';
import { useListContext } from 'react-admin';
import { DefaultValues, FieldValues } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { useApplyFiltersToUrl } from 'shared/mui/Filter/hooks/useApplyFiltersToUrl';
import { FilterIdsValues } from 'shared/mui/Filter/hooks/useIdFilters';
import { cleanEmpty } from 'utils/cleanEmpty';

type Props<TFieldValues extends FieldValues> = {
  appliedFilters?: DefaultValues<TFieldValues>;
  appliedIdFilters?: FilterIdsValues;
  transform?: (appliedFilters: DefaultValues<TFieldValues>) => any;
  transformId?: (appliedIdFilters: FilterIdsValues) => any;
  enabled?: boolean;
};

export function useSyncFilter<TFieldValues extends FieldValues>({
  appliedFilters,
  appliedIdFilters,
  transform,
  transformId,
  enabled = true,
}: Props<TFieldValues>) {
  const { setFilters, filterValues, displayedFilters } = useListContext();
  const prevFilterValues = usePrevious(JSON.stringify(filterValues ?? {}));
  const location = useLocation();
  const { applyFiltersToUrl } = useApplyFiltersToUrl();

  const isUrlChangedExternally = () => {
    return (
      prevFilterValues !== undefined && prevFilterValues !== JSON.stringify(filterValues ?? {})
    );
  };

  const defaultTransform = (filters: DefaultValues<TFieldValues>) =>
    cleanEmpty({ ...filters }, false);

  const getAllAppliedFilters = () => ({
    ...(appliedFilters ? (transform ?? defaultTransform)(appliedFilters) : {}),
    ...(appliedIdFilters ? (transformId ? transformId(appliedIdFilters) : appliedIdFilters) : {}),
  });

  // Записываем дефолтные фильтры в URL только при первом открытии списка без filter.
  // Например: /companies → /companies?filter={"archive":false}
  // Используем replace, чтобы не создавать лишнюю запись в history
  // поэтому для возврата назад достаточно одного клика.
  useEffectOnce(() => {
    if (!enabled) {
      return;
    }

    const cleanCombined = cleanEmpty(getAllAppliedFilters(), false);

    if (
      new URLSearchParams(location.search).has('filter') ||
      Object.keys(cleanCombined).length === 0
    ) {
      return;
    }

    applyFiltersToUrl(cleanCombined);
  });

  useEffect(() => {
    if (!enabled) {
      return;
    }

    // URL изменился навигацией (назад/вперёд/refresh)
    // Форму восстановит useFilters.
    if (isUrlChangedExternally()) {
      return;
    }

    const allAppliedFilters = getAllAppliedFilters();
    if (isEqual(cleanEmpty(allAppliedFilters, false), cleanEmpty(filterValues ?? {}, false))) {
      return;
    }

    setFilters(allAppliedFilters, displayedFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    JSON.stringify(appliedFilters),
    JSON.stringify(appliedIdFilters),
    JSON.stringify(filterValues),
  ]);
}
